import React, { useEffect } from "react";
import {
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { Link } from "gatsby";
import { useAuthContext } from "../../../contexts/AuthProvider";
import PostIcon from "@/assets/icons/PostIcon";
import Request from "./icons/Request";
import RequestIcon from "./icons/RequestIcon";
import CircleIcon from "./icons/CircleIcon";
import Complaint from "./icons/Claim";
import CircleExclamationIcon from "./icons/ExclamationIcon";
import NewIcon from "./icons/NewIcon";
import Check from "./icons/Check";
import Apply from "./icons/Apply";
import { Mailbox } from "lucide-react";

function SidebarLink({ to, icon: Icon, children, iconComponent, onClick }: { to: string; icon?: any; children: React.ReactNode; iconComponent?: React.ReactNode; onClick?: () => void }) {
  const isActive = typeof window !== "undefined" && window.location.pathname.replace(/\/$/, "") === to.replace(/\/$/, "");
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg text-sm font-medium no-underline transition-all duration-200
        ${isActive
          ? "bg-blue-600/15 text-blue-400 shadow-sm"
          : "text-gray-400 hover:bg-gray-700/50 hover:text-gray-100"
        }`}
    >
      <span className={`flex-shrink-0 transition-colors duration-200 ${isActive ? "text-blue-400" : "text-gray-500 group-hover:text-gray-300"}`}>
        {iconComponent ? iconComponent : Icon ? <Icon className="w-5 h-5" /> : null}
      </span>
      <span className="truncate">{children}</span>
    </Link>
  );
}

function SidebarSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-5 first:mt-0">
      <h3 className="mb-2 px-5 font-semibold text-[11px] text-gray-500 uppercase tracking-wider">
        {label}
      </h3>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export default function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { user, setUser } = useAuthContext();

  return (
    <div
      className={`fixed top-[var(--admin-navbar-height)] left-0 z-40 flex flex-col w-[260px] h-[calc(100vh-var(--admin-navbar-height))] overflow-y-auto border-r border-gray-800/60 bg-gray-900 text-white sidebar transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
    >
      {/* Navigation */}
      <div className="space-y-1 w-full px-1 pt-6 pb-6">
        <SidebarSection label="Overview">
          <SidebarLink to="/admin" icon={HomeIcon} onClick={onClose}>Dashboard</SidebarLink>
        </SidebarSection>

        {(user as any)?.role == 1 && (
          <SidebarSection label="Users">
            <SidebarLink to="/admin/users" icon={UsersIcon} onClick={onClose}>Users</SidebarLink>
            <SidebarLink to="/admin/users-requests" iconComponent={<RequestIcon />} onClick={onClose}>User Requests</SidebarLink>
          </SidebarSection>
        )}

        <SidebarSection label="Management">
          <SidebarLink to="/admin/applies" iconComponent={<Apply className="w-5 h-5" aria-hidden="true" />} onClick={onClose}>Applies</SidebarLink>
          <SidebarLink to="/admin/complaints" iconComponent={<CircleExclamationIcon />} onClick={onClose}>Complaints</SidebarLink>
          <SidebarLink to="/admin/feedbacks" iconComponent={<Complaint />} onClick={onClose}>Feedbacks</SidebarLink>
          <SidebarLink to="/admin/customer-feedbacks" icon={ChatBubbleLeftRightIcon} onClick={onClose}>Customer Feedbacks</SidebarLink>
          <SidebarLink to="/admin/questions" iconComponent={<CircleIcon />} onClick={onClose}>Questions</SidebarLink>
          <SidebarLink to="/admin/checks" iconComponent={<Check />} onClick={onClose}>Checks</SidebarLink>
        </SidebarSection>

        <SidebarSection label="Applications">
          <SidebarLink to="/admin/saudi-applications" iconComponent={<NewIcon />} onClick={onClose}>Saudi Applications</SidebarLink>
          <SidebarLink to="/admin/estimates" iconComponent={<Request />} onClick={onClose}>Estimate Requests</SidebarLink>
        </SidebarSection>

        <SidebarSection label="Content">
          <SidebarLink to="/admin/posts" iconComponent={<PostIcon className="text-gray-500 group-hover:text-gray-300 transition-colors duration-200" style={{ width: "20px", height: "20px" }} />} onClick={onClose}>Posts</SidebarLink>
          <SidebarLink to="/admin/certificates" icon={ClipboardDocumentCheckIcon} onClick={onClose}>Certificates</SidebarLink>
          <SidebarLink to="/admin/newsletter" iconComponent={<Mailbox className="w-5 h-5" />} onClick={onClose}>Newsletters</SidebarLink>
        </SidebarSection>

        <SidebarSection label="Quiz">
          <SidebarLink to="/admin/quiz" iconComponent={<QuizIcon className="w-5 h-5" />} onClick={onClose}>Quiz</SidebarLink>
          <SidebarLink to="/admin/quiz-results" iconComponent={<QuizIcon className="w-5 h-5" />} onClick={onClose}>Quiz Results</SidebarLink>
        </SidebarSection>
      </div>
    </div>
  );
}

function QuizIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" color="currentColor" fill="none" {...props}>
      <path
        d="M7.5 3.5C5.9442 3.54667 5.01661 3.71984 4.37477 4.36227C3.49609 5.24177 3.49609 6.6573 3.49609 9.48836L3.49609 15.9944C3.49609 18.8255 3.49609 20.241 4.37477 21.1205C5.25345 22 6.66767 22 9.49609 22L14.4961 22C17.3245 22 18.7387 22 19.6174 21.1205C20.4961 20.241 20.4961 18.8255 20.4961 15.9944V9.48836C20.4961 6.6573 20.4961 5.24177 19.6174 4.36228C18.9756 3.71984 18.048 3.54667 16.4922 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M7.49609 3.75C7.49609 2.7835 8.2796 2 9.24609 2H14.7461C15.7126 2 16.4961 2.7835 16.4961 3.75C16.4961 4.7165 15.7126 5.5 14.7461 5.5H9.24609C8.2796 5.5 7.49609 4.7165 7.49609 3.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M6.5 10L10.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M13.5 11C13.5 11 14 11 14.5 12C14.5 12 16.0882 9.5 17.5 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M6.5 16L10.5 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M13.5 17C13.5 17 14 17 14.5 18C14.5 18 16.0882 15.5 17.5 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
